import React, { useState } from "react"
import { Container, Row, Col, Card, CardBody, Collapse } from "reactstrap"
import { HelpCircle, ChevronRight } from "lucide-react"
import SEO from "../../components/Common/SEO"
import { generateCanonicalUrl } from "../../utils/seoUtils"

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(0)

    const toggle = (index) => {
        setOpenIndex(openIndex === index ? -1 : index)
    }

    const faqs = [
        {
            question: "What is KeyQuest?",
            answer: "KeyQuest is a comprehensive analytics and market intelligence platform specifically designed for XR (Extended Reality) and VR (Virtual Reality) developers. We provide tools for keyword tracking, market analysis, category exploration, and AI-driven insights to help you succeed in the VR ecosystem."
        },
        {
            question: "How accurate is the data?",
            answer: "We pull real-time data directly from major VR platforms and store fronts. Our algorithms process thousands of data points daily to provide you with the most accurate and up-to-date ranking, keyword performance, and market trend information available in the industry."
        },
        {
            question: "Can I track my competitors?",
            answer: "Yes! Our platform allows you to track any app in the VR ecosystem. You can add competitor apps to your tracking lists to monitor their rankings, keyword performance, and updates, giving you a competitive edge in market positioning."
        },
        {
            question: "What are AI Insights?",
            answer: "AI Insights uses advanced machine learning models to analyze market trends, user sentiment, and competitor movements. It provides actionable recommendations on keyword optimization, feature prioritization, and market entry strategies based on complex data patterns that might be missed manually."
        },
        {
            question: "Is there a free trial?",
            answer: "We offer various subscription plans tailored to different needs, from individual indie developers to large studios. Please check our Plans & Billing section for details on available tiers and features included in each."
        }
    ]

    const pageBg = "#171821"
    const cardBg = "#21222D"
    const borderColor = "#353748"
    const accentColor = "#ff2f6d"

    return (
    <React.Fragment>
        <SEO
            title="FAQ | KeyQuest VR Analytics Platform"
            description="Find answers to frequently asked questions about KeyQuest VR analytics platform. Learn about VR app tracking, keyword research, market insights, and developer tools."
            keywords="KeyQuest FAQ, VR analytics questions, Meta Quest analytics help, VR app tracking support, keyword research FAQ"
            canonical={generateCanonicalUrl('/faq')}
            robots="index, follow"
        />
        <div className="page-content" style={{ backgroundColor: pageBg, minHeight: "100vh", fontFamily: "Rubik, sans-serif" }}>
            <Container fluid>
                {/* Hero Section */}
                <Row className="justify-content-center mb-5 mt-4">
                    <Col lg={10} className="text-center">
                        <div className="mb-3 d-inline-flex align-items-center justify-content-center"
                            style={{ width: "60px", height: "60px", backgroundColor: "rgba(255, 47, 109, 0.1)", borderRadius: "15px" }}>
                            <HelpCircle size={32} style={{ color: accentColor }} />
                        </div>
                        <h1 className="text-white mb-3" style={{ fontWeight: 700, fontSize: "clamp(28px, 4vw, 42px)", letterSpacing: "-0.02em" }}>
                            Frequently Asked Questions
                        </h1>
                        <p className="mx-auto" style={{ color: "#a1acb8", fontSize: "1.1rem", maxWidth: "600px", lineHeight: "1.6" }}>
                            Everything you need to know about KeyQuest platform and how to use it.
                        </p>
                    </Col>
                </Row>

                <Row className="justify-content-center">
                    <Col lg={8}>
                        <div className="faq-accordion">
                            {faqs.map((faq, index) => (
                                <Card
                                    key={index}
                                    className="mb-3"
                                    style={{
                                        backgroundColor: cardBg,
                                        border: `1px solid ${borderColor}`,
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    <div
                                        className="p-4 d-flex align-items-center justify-content-between"
                                        onClick={() => toggle(index)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <h2 className="text-white m-0" style={{ fontWeight: 600, fontSize: "1.05rem", lineHeight: "1.4" }}>
                                            {faq.question}
                                        </h2>
                                        <div
                                            style={{
                                                transition: "transform 0.3s ease",
                                                transform: openIndex === index ? "rotate(90deg)" : "rotate(0deg)",
                                                color: openIndex === index ? accentColor : "rgba(255,255,255,0.4)"
                                            }}
                                        >
                                            <ChevronRight size={20} />
                                        </div>
                                    </div>
                                    <Collapse isOpen={openIndex === index}>
                                        <CardBody className="pt-0 pb-4 px-4">
                                            <div style={{
                                                height: "1px",
                                                backgroundColor: "rgba(255,255,255,0.06)",
                                                marginBottom: "20px"
                                            }}></div>
                                            <p className="m-0" style={{ color: "#a1acb8", fontSize: "1rem", lineHeight: "1.7" }}>
                                                {faq.answer}
                                            </p>
                                        </CardBody>
                                    </Collapse>
                                </Card>
                            ))}
                        </div>
                    </Col>
                </Row>

                {/* Still have questions? Section */}
                <Row className="justify-content-center mt-5 mb-5">
                    <Col lg={8} className="text-center">
                        <div className="p-4 rounded-4" style={{ backgroundColor: "rgba(255,255,255,0.02)", border: `1px dashed ${borderColor}` }}>
                            <h2 className="text-white mb-2" style={{ fontWeight: 600, fontSize: '18px' }}>Still have questions?</h2>
                            <p style={{ color: "#a1acb8", marginBottom: "20px" }}>
                                Can't find the answer you're looking for? Please chat with our AI or reach out to our support system.
                            </p>
                            <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
                                <a
                                    href="/chatbot"
                                    className="btn"
                                    style={{
                                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                                        color: "#fff",
                                        border: `1px solid ${borderColor}`,
                                        borderRadius: "10px",
                                        padding: "10px 25px",
                                        fontWeight: 600,
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    Chat with AI
                                </a>
                                <a
                                    href="/support"
                                    className="btn"
                                    style={{
                                        backgroundColor: accentColor,
                                        color: "#fff",
                                        borderRadius: "10px",
                                        padding: "10px 25px",
                                        fontWeight: 600,
                                        boxShadow: `0 4px 15px rgba(255, 47, 109, 0.2)`
                                    }}
                                >
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <style>{`
                .faq-accordion .card:hover {
                    border-color: rgba(255, 47, 109, 0.4) !important;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(0,0,0,0.2);
                }
                .btn:hover {
                    opacity: 0.9;
                    transform: translateY(-1px);
                    color: white;
                }
            `}</style>
        </div>
    </React.Fragment>
    )
}

export default FAQ
